import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import 'chart.js/auto';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MetricCard from 'components/MetricCard';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';
import DateFilterPresets from 'components/DateFilterPresets';
import EntityFilter from 'components/EntityFilter';
import usePersistedFilters from 'hooks/usePersistedFilters';
import { getMultiplayerLiquidityReport } from 'services/analytics';

const numberFmt = new Intl.NumberFormat('en-US');
const pctFmt = (value) => `${Number(value || 0).toFixed(1)}%`;
const msToSec = (value) => `${Math.round(Number(value || 0) / 1000)}s`;
const msToMin = (value) => `${(Number(value || 0) / 60000).toFixed(1)}m`;

const DROPOFF_LABELS = {
  played_at_least_one_bet: 'Played (≥1 bet)',
  no_peer_same_game_betlimit_on_join: 'No opponent at join',
  left_before_room_selection: 'Left before room',
  table_assigned_but_no_peer_on_same_table: 'Room assigned, no peer',
  peer_existed_but_no_bet: 'Peer existed, no bet'
};

const DROPOFF_COLORS = {
  played_at_least_one_bet: '#2ed573',
  no_peer_same_game_betlimit_on_join: '#ff4757',
  left_before_room_selection: '#ffa502',
  table_assigned_but_no_peer_on_same_table: '#22d3ee',
  peer_existed_but_no_bet: '#a55eea'
};

function HourlyHeatmap({ hourly }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const lookup = useMemo(() => {
    const map = {};
    (hourly || []).forEach((row) => {
      map[`${row.isoWeekday}-${row.hourOfDay}`] = row;
    });
    return map;
  }, [hourly]);

  const maxSessions = useMemo(
    () => Math.max(1, ...(hourly || []).map((r) => Number(r.sessionsTotal || 0))),
    [hourly]
  );

  const getCellColor = (row) => {
    if (!row) return 'rgba(255,255,255,0.02)';
    const intensity = Math.min(1, Number(row.sessionsTotal || 0) / maxSessions);
    const playRate = Number(row.playRatePct || 0);
    if (playRate >= 20) return `rgba(46, 213, 115, ${0.1 + intensity * 0.5})`;
    if (playRate >= 5) return `rgba(255, 165, 2, ${0.1 + intensity * 0.45})`;
    return `rgba(255, 71, 87, ${0.08 + intensity * 0.4})`;
  };

  return (
    <MDBox sx={{ overflowX: 'auto' }}>
      <MDBox sx={{ display: 'grid', gridTemplateColumns: `56px repeat(24, 1fr)`, gap: '3px', minWidth: 700 }}>
        <MDBox />
        {hours.map((h) => (
          <MDTypography key={h} variant='caption' color='text' sx={{ textAlign: 'center', fontSize: '0.6rem' }}>
            {h.toString().padStart(2, '0')}
          </MDTypography>
        ))}
        {weekdays.map((day, dayIdx) => (
          <Fragment key={day}>
            <MDTypography
              variant='caption'
              color='text'
              fontWeight='medium'
              sx={{ display: 'flex', alignItems: 'center', fontSize: '0.68rem' }}
            >
              {day}
            </MDTypography>
            {hours.map((h) => {
              const row = lookup[`${dayIdx + 1}-${h}`];
              return (
                <Tooltip
                  key={`${dayIdx + 1}-${h}`}
                  title={
                    row
                      ? `${row.sessionsTotal} sessions · ${pctFmt(row.playRatePct)} play rate · ${pctFmt(row.joinedAloneSameBetlimitPct)} alone`
                      : 'No data'
                  }
                  arrow
                >
                  <MDBox
                    sx={{
                      background: getCellColor(row),
                      borderRadius: '4px',
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'default',
                      transition: 'transform 0.15s',
                      '&:hover': { transform: 'scale(1.15)', zIndex: 2 }
                    }}
                  >
                    <MDTypography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.7)' }}>
                      {row ? row.sessionsTotal : ''}
                    </MDTypography>
                  </MDBox>
                </Tooltip>
              );
            })}
          </Fragment>
        ))}
      </MDBox>
      <MDBox display='flex' gap={2} mt={1.5} justifyContent='center'>
        <MDBox display='flex' alignItems='center' gap={0.5}>
          <MDBox sx={{ width: 12, height: 12, borderRadius: '3px', background: 'rgba(46,213,115,0.45)' }} />
          <MDTypography variant='caption' color='text' sx={{ fontSize: '0.62rem' }}>≥20% play rate</MDTypography>
        </MDBox>
        <MDBox display='flex' alignItems='center' gap={0.5}>
          <MDBox sx={{ width: 12, height: 12, borderRadius: '3px', background: 'rgba(255,165,2,0.4)' }} />
          <MDTypography variant='caption' color='text' sx={{ fontSize: '0.62rem' }}>5–20% play rate</MDTypography>
        </MDBox>
        <MDBox display='flex' alignItems='center' gap={0.5}>
          <MDBox sx={{ width: 12, height: 12, borderRadius: '3px', background: 'rgba(255,71,87,0.35)' }} />
          <MDTypography variant='caption' color='text' sx={{ fontSize: '0.62rem' }}>&lt;5% play rate</MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

function MultiplayerLiquidityDashboard() {
  const { filters, setDateRange, setEntityFilters } = usePersistedFilters();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        clientId: filters.clientIds?.[0] || undefined,
        operatorId: filters.operatorIds?.[0] || undefined,
        brandId: filters.brandIds?.[0] || undefined,
        timezone: 'UTC',
        breakdownLimit: 50
      };
      const res = await getMultiplayerLiquidityReport(apiFilters);
      setReport(res?.data || null);
    } catch (error) {
      console.error('Error fetching multiplayer liquidity report:', error);
    }
    setLoading(false);
  }, [filters.startDate, filters.endDate, filters.clientIds, filters.operatorIds, filters.brandIds]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summary = report?.summary || {};
  const daily = report?.daily || [];
  const hourly = report?.hourly || [];
  const operators = report?.operators || [];
  const providers = report?.providers || [];
  const dropoffReasons = report?.dropoffReasons || [];
  const minPlayersPressure = report?.minPlayersPressure || [];

  const dailyLabels = useMemo(
    () => daily.map((row) => dayjs(row.dayLocal).format('MMM D')),
    [daily]
  );

  const dailyVolumeData = useMemo(
    () => ({
      labels: dailyLabels,
      datasets: [
        {
          label: 'Total Sessions',
          data: daily.map((row) => Number(row.sessionsTotal || 0)),
          borderColor: '#22d3ee',
          backgroundColor: 'rgba(34,211,238,0.12)',
          tension: 0.3,
          fill: true,
          pointRadius: daily.length > 60 ? 0 : 3,
          borderWidth: 2
        },
        {
          label: 'Sessions With Bet',
          data: daily.map((row) => Number(row.sessionsWithBet || 0)),
          borderColor: '#28c68d',
          backgroundColor: 'rgba(40,198,141,0.10)',
          tension: 0.3,
          fill: true,
          pointRadius: daily.length > 60 ? 0 : 3,
          borderWidth: 2
        }
      ]
    }),
    [daily, dailyLabels]
  );

  const dailyRateData = useMemo(
    () => ({
      labels: dailyLabels,
      datasets: [
        {
          label: 'No-Bet Rate %',
          data: daily.map((row) => Number(row.noBetRatePct || 0)),
          borderColor: '#ff4f69',
          backgroundColor: 'rgba(255,79,105,0.10)',
          tension: 0.3,
          fill: true,
          pointRadius: daily.length > 60 ? 0 : 3,
          borderWidth: 2
        },
        {
          label: 'Joined Alone %',
          data: daily.map((row) => Number(row.joinedAloneSameBetlimitPct || 0)),
          borderColor: '#e8a22a',
          backgroundColor: 'rgba(232,162,42,0.10)',
          tension: 0.3,
          fill: true,
          pointRadius: daily.length > 60 ? 0 : 3,
          borderWidth: 2
        }
      ]
    }),
    [daily, dailyLabels]
  );

  const topOperatorRows = useMemo(() => operators.slice(0, 10), [operators]);
  const operatorBarData = useMemo(
    () => ({
      labels: topOperatorRows.map((row) =>
        (row.operatorName || row.operatorId || '').length > 18
          ? (row.operatorName || row.operatorId || '').slice(0, 18) + '…'
          : row.operatorName || row.operatorId
      ),
      datasets: [
        {
          label: 'Sessions',
          data: topOperatorRows.map((row) => Number(row.sessionsTotal || 0)),
          backgroundColor: 'rgba(34,211,238,0.55)',
          borderColor: '#22d3ee',
          borderWidth: 1,
          borderRadius: 4
        }
      ]
    }),
    [topOperatorRows]
  );

  const dropoffDoughnutData = useMemo(
    () => ({
      labels: dropoffReasons.map((r) => DROPOFF_LABELS[r.dropoffProxy] || r.dropoffProxy),
      datasets: [
        {
          data: dropoffReasons.map((r) => Number(r.sessionsTotal || 0)),
          backgroundColor: dropoffReasons.map(
            (r) => DROPOFF_COLORS[r.dropoffProxy] || '#6b7194'
          ),
          borderWidth: 0,
          hoverOffset: 6
        }
      ]
    }),
    [dropoffReasons]
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#8a9bc2', font: { size: 11 } }
      }
    },
    scales: {
      x: {
        ticks: { color: '#6b7e9e', font: { size: 10 } },
        grid: { color: 'rgba(90,104,145,0.12)' }
      },
      y: {
        beginAtZero: true,
        ticks: { color: '#6b7e9e', font: { size: 10 } },
        grid: { color: 'rgba(90,104,145,0.12)' }
      }
    }
  };

  const periodLabel = report?.period
    ? `${dayjs(report.period.startDate).format('MMM D, YYYY')} → ${dayjs(report.period.endDate).format('MMM D, YYYY')} UTC`
    : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1, md: 3 }}>

        {/* HERO BANNER */}
        <Card sx={{ mb: 3, overflow: 'hidden', position: 'relative' }}>
          <MDBox
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #e2001b, #ff4f69, transparent 80%)'
            }}
          />
          <MDBox p={2.5}>
            <MDBox display='flex' alignItems='center' gap={1} mb={0.5}>
              <Icon sx={{ color: 'error.main', fontSize: '1.6rem' }}>groups</Icon>
              <MDTypography variant='h5' fontWeight='bold'>
                Multiplayer Liquidity &amp; Matchmaking
              </MDTypography>
            </MDBox>
            <MDTypography variant='button' color='text' display='block'>
              {loading ? (
                <Skeleton width='60%' />
              ) : periodLabel}
            </MDTypography>
            {!loading && summary.sessionsTotal > 0 && (
              <MDBox mt={1.5} display='flex' flexWrap='wrap' gap={1.5}>
                <Chip
                  size='small'
                  icon={<Icon sx={{ fontSize: '0.9rem !important' }}>warning</Icon>}
                  label={`${pctFmt(summary.noBetRatePct)} no-bet rate`}
                  sx={{
                    bgcolor: 'rgba(255,79,105,0.12)',
                    color: '#ff6b78',
                    fontWeight: 600,
                    fontSize: '0.72rem'
                  }}
                />
                <Chip
                  size='small'
                  icon={<Icon sx={{ fontSize: '0.9rem !important' }}>person_off</Icon>}
                  label={`${pctFmt(summary.joinedAloneSameBetlimitPct)} joined alone`}
                  sx={{
                    bgcolor: 'rgba(232,162,42,0.12)',
                    color: '#e8a22a',
                    fontWeight: 600,
                    fontSize: '0.72rem'
                  }}
                />
                <Chip
                  size='small'
                  icon={<Icon sx={{ fontSize: '0.9rem !important' }}>trending_up</Icon>}
                  label={`Peak ${summary.peakConcurrentPlayers} concurrent`}
                  sx={{
                    bgcolor: 'rgba(34,211,238,0.12)',
                    color: '#22d3ee',
                    fontWeight: 600,
                    fontSize: '0.72rem'
                  }}
                />
              </MDBox>
            )}
          </MDBox>
        </Card>

        {/* FILTERS */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2.5}>
            <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={1.5}>
              <MDTypography variant='h6' fontWeight='bold'>
                Filters
              </MDTypography>
              <MDButton variant='outlined' color='info' size='small' onClick={fetchData} disabled={loading}>
                <Icon sx={{ mr: 0.5 }}>refresh</Icon>
                {loading ? 'Loading…' : 'Refresh'}
              </MDButton>
            </MDBox>

            <MDBox mb={2}>
              <MDTypography variant='button' color='text' fontWeight='regular' mb={1}>
                Date Range
              </MDTypography>
              <DateFilterPresets
                onDateChange={(startDate, endDate, preset) => setDateRange(startDate, endDate, preset)}
                preset={filters.preset || 'last_30_days'}
                startDate={filters.startDate}
                endDate={filters.endDate}
              />
            </MDBox>

            <MDTypography variant='button' color='text' fontWeight='regular' mb={1}>
              Entity Filters
            </MDTypography>
            <EntityFilter
              onFilterChange={setEntityFilters}
              initialValues={{
                clientIds: filters.clientIds,
                operatorIds: filters.operatorIds,
                brandIds: filters.brandIds
              }}
            />
          </MDBox>
          {loading && <LinearProgress color='info' />}
        </Card>

        {/* KPI ROW */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title='Total Sessions'
              icon='confirmation_number'
              iconColor='info'
              value={numberFmt.format(summary.sessionsTotal || 0)}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title='Unique Players'
              icon='people'
              iconColor='dark'
              value={numberFmt.format(summary.uniquePlayers || 0)}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title='Play Rate'
              icon='sports_esports'
              iconColor={summary.playRatePct >= 20 ? 'success' : 'warning'}
              value={pctFmt(summary.playRatePct)}
              subtitle='Sessions with ≥1 bet'
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title='No-Bet Rate'
              icon='block'
              iconColor={summary.noBetRatePct >= 70 ? 'error' : 'warning'}
              value={pctFmt(summary.noBetRatePct)}
              subtitle='Sessions without any bet'
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title='Joined Alone'
              icon='person_off'
              iconColor={summary.joinedAloneSameBetlimitPct >= 55 ? 'error' : 'warning'}
              value={pctFmt(summary.joinedAloneSameBetlimitPct)}
              subtitle='No peer at same bet limit on join'
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title='Avg Concurrency'
              icon='group_add'
              iconColor='info'
              value={(summary.avgJoinConcurrencySameBetlimit || 0).toFixed(2)}
              subtitle='Avg peers at join moment'
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title='Peak Concurrent'
              icon='show_chart'
              iconColor='info'
              value={numberFmt.format(summary.peakConcurrentPlayers || 0)}
              subtitle='Max simultaneous players'
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title='Quick Bounces'
              icon='timer'
              iconColor='warning'
              value={numberFmt.format(summary.noBetUnder30s || 0)}
              subtitle={`${pctFmt(summary.noBetUnder30sRatePct)} of no-bet, under 30s`}
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* CHARTS ROW 1 — Volume + Top Operators */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ height: '100%' }}>
              <MDBox p={2}>
                <MDBox display='flex' justifyContent='space-between' alignItems='center'>
                  <MDTypography variant='h6' fontWeight='bold'>
                    Daily Session Volume
                  </MDTypography>
                  <MDTypography variant='caption' color='text'>
                    Total vs sessions with bet
                  </MDTypography>
                </MDBox>
                <MDBox mt={1.5} height='280px'>
                  {loading ? (
                    <Skeleton variant='rectangular' height='100%' sx={{ borderRadius: 2 }} />
                  ) : (
                    <Line data={dailyVolumeData} options={chartOptions} />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: '100%' }}>
              <MDBox p={2}>
                <MDTypography variant='h6' fontWeight='bold'>
                  Top Operators
                </MDTypography>
                <MDBox mt={1.5} height='280px'>
                  {loading ? (
                    <Skeleton variant='rectangular' height='100%' sx={{ borderRadius: 2 }} />
                  ) : (
                    <Bar
                      data={operatorBarData}
                      options={{
                        ...chartOptions,
                        indexAxis: 'y',
                        plugins: { legend: { display: false } }
                      }}
                    />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* CHARTS ROW 2 — Rates + Dropoff Doughnut */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ height: '100%' }}>
              <MDBox p={2}>
                <MDBox display='flex' justifyContent='space-between' alignItems='center'>
                  <MDTypography variant='h6' fontWeight='bold'>
                    Daily Risk Rates
                  </MDTypography>
                  <MDTypography variant='caption' color='text'>
                    No-bet rate &amp; joined-alone rate
                  </MDTypography>
                </MDBox>
                <MDBox mt={1.5} height='280px'>
                  {loading ? (
                    <Skeleton variant='rectangular' height='100%' sx={{ borderRadius: 2 }} />
                  ) : (
                    <Line
                      data={dailyRateData}
                      options={{
                        ...chartOptions,
                        scales: {
                          ...chartOptions.scales,
                          y: { ...chartOptions.scales.y, max: 100 }
                        }
                      }}
                    />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: '100%' }}>
              <MDBox p={2}>
                <MDTypography variant='h6' fontWeight='bold' mb={1}>
                  Session Outcome Mix
                </MDTypography>
                {loading ? (
                  <Skeleton variant='circular' width={200} height={200} sx={{ mx: 'auto', mt: 3 }} />
                ) : (
                  <>
                    <MDBox height='220px' display='flex' justifyContent='center'>
                      <Doughnut
                        data={dropoffDoughnutData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          cutout: '62%',
                          plugins: {
                            legend: { display: false }
                          }
                        }}
                      />
                    </MDBox>
                    <MDBox mt={1.5} display='flex' flexDirection='column' gap={0.8}>
                      {dropoffReasons.map((row) => (
                        <MDBox key={row.dropoffProxy} display='flex' justifyContent='space-between' alignItems='center'>
                          <MDBox display='flex' alignItems='center' gap={0.8}>
                            <MDBox
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: DROPOFF_COLORS[row.dropoffProxy] || '#6b7194',
                                flexShrink: 0
                              }}
                            />
                            <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.7rem' }}>
                              {DROPOFF_LABELS[row.dropoffProxy] || row.dropoffProxy}
                            </MDTypography>
                          </MDBox>
                          <MDTypography variant='caption' color='text' sx={{ fontSize: '0.68rem' }}>
                            {numberFmt.format(row.sessionsTotal)} ({pctFmt(row.shareOfSessionsPct)})
                          </MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* HOURLY HEATMAP */}
        {hourly.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <MDBox p={2}>
              <MDBox display='flex' alignItems='center' gap={1} mb={1.5}>
                <Icon sx={{ color: 'text.main' }}>schedule</Icon>
                <MDTypography variant='h6' fontWeight='bold'>
                  Hourly Activity Heatmap
                </MDTypography>
                <MDTypography variant='caption' color='text' ml={1}>
                  Color = play rate · brightness = volume
                </MDTypography>
              </MDBox>
              <HourlyHeatmap hourly={hourly} />
            </MDBox>
          </Card>
        )}

        {/* OPERATOR TABLE */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2}>
            <MDBox display='flex' alignItems='center' gap={1} mb={1.5}>
              <Icon sx={{ color: 'text.main' }}>business</Icon>
              <MDTypography variant='h6' fontWeight='bold'>
                Operator / Client Breakdown
              </MDTypography>
            </MDBox>
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Operator</TableCell>
                    <TableCell align='right'>Sessions</TableCell>
                    <TableCell align='right'>Players</TableCell>
                    <TableCell align='right'>Play Rate</TableCell>
                    <TableCell align='right'>No-Bet</TableCell>
                    <TableCell align='right'>Joined Alone</TableCell>
                    <TableCell align='right'>Avg Concurrency</TableCell>
                    <TableCell align='right'>Avg Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((__, j) => (
                          <TableCell key={j}><Skeleton variant='text' /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    operators.slice(0, 25).map((row) => (
                      <TableRow key={`${row.operatorId}-${row.operatorName}`} hover>
                        <TableCell>
                          <MDTypography variant='button' fontWeight='medium' sx={{ fontSize: '0.78rem' }}>
                            {row.operatorName || row.operatorId}
                          </MDTypography>
                        </TableCell>
                        <TableCell align='right'>{numberFmt.format(row.sessionsTotal || 0)}</TableCell>
                        <TableCell align='right'>{numberFmt.format(row.uniquePlayers || 0)}</TableCell>
                        <TableCell align='right'>
                          <Chip
                            size='small'
                            label={pctFmt(row.playRatePct)}
                            sx={{
                              bgcolor: row.playRatePct >= 20 ? 'rgba(40,198,141,0.15)' : 'rgba(255,165,2,0.15)',
                              color: row.playRatePct >= 20 ? '#28c68d' : '#e8a22a',
                              fontWeight: 600,
                              fontSize: '0.68rem',
                              height: 22
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>{pctFmt(row.noBetRatePct)}</TableCell>
                        <TableCell align='right'>
                          <Chip
                            size='small'
                            label={pctFmt(row.joinedAloneSameBetlimitPct)}
                            sx={{
                              bgcolor: row.joinedAloneSameBetlimitPct >= 55
                                ? 'rgba(255,79,105,0.15)'
                                : 'rgba(255,165,2,0.15)',
                              color: row.joinedAloneSameBetlimitPct >= 55 ? '#ff6b78' : '#e8a22a',
                              fontWeight: 600,
                              fontSize: '0.68rem',
                              height: 22
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>{Number(row.avgJoinConcurrencySameBetlimit || 0).toFixed(2)}</TableCell>
                        <TableCell align='right'>{msToMin(row.avgDurationMs)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>

        {/* PROVIDERS TABLE */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2}>
            <MDBox display='flex' alignItems='center' gap={1} mb={1.5}>
              <Icon sx={{ color: 'text.main' }}>hub</Icon>
              <MDTypography variant='h6' fontWeight='bold'>
                Provider Breakdown
              </MDTypography>
            </MDBox>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell align='right'>Sessions</TableCell>
                    <TableCell align='right'>Players</TableCell>
                    <TableCell align='right'>Play Rate</TableCell>
                    <TableCell align='right'>No-Bet</TableCell>
                    <TableCell align='right'>Joined Alone</TableCell>
                    <TableCell align='right'>Avg Concurrency</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((__, j) => (
                          <TableCell key={j}><Skeleton variant='text' /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    providers.slice(0, 20).map((row) => (
                      <TableRow key={row.provider} hover>
                        <TableCell>
                          <MDTypography variant='button' fontWeight='medium' sx={{ fontSize: '0.78rem' }}>
                            {row.provider}
                          </MDTypography>
                        </TableCell>
                        <TableCell align='right'>{numberFmt.format(row.sessionsTotal || 0)}</TableCell>
                        <TableCell align='right'>{numberFmt.format(row.uniquePlayers || 0)}</TableCell>
                        <TableCell align='right'>{pctFmt(row.playRatePct)}</TableCell>
                        <TableCell align='right'>{pctFmt(row.noBetRatePct)}</TableCell>
                        <TableCell align='right'>{pctFmt(row.joinedAloneSameBetlimitPct)}</TableCell>
                        <TableCell align='right'>{Number(row.avgJoinConcurrencySameBetlimit || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>

        {/* MIN PLAYERS PRESSURE */}
        {minPlayersPressure.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <MDBox p={2}>
              <MDBox display='flex' alignItems='center' gap={1} mb={0.5}>
                <Icon sx={{ color: 'text.main' }}>psychology</Icon>
                <MDTypography variant='h6' fontWeight='bold'>
                  Game Min-Players Pressure
                </MDTypography>
              </MDBox>
              <MDTypography variant='caption' color='text' display='block' mb={2}>
                How often players join a game that hasn&apos;t reached its minimum required player count.
              </MDTypography>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Game</TableCell>
                      <TableCell align='right'>Min Players</TableCell>
                      <TableCell align='right'>Sessions</TableCell>
                      <TableCell align='right'>Below Min on Join</TableCell>
                      <TableCell align='right'>Play Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {minPlayersPressure.map((row) => (
                      <TableRow key={row.gameId} hover>
                        <TableCell>
                          <MDTypography variant='button' fontWeight='medium' sx={{ fontSize: '0.78rem' }}>
                            {row.gameName}
                          </MDTypography>
                        </TableCell>
                        <TableCell align='right'>{row.gameMinPlayers}</TableCell>
                        <TableCell align='right'>{numberFmt.format(row.sessionsTotal || 0)}</TableCell>
                        <TableCell align='right'>
                          <Chip
                            size='small'
                            label={pctFmt(row.belowMinPlayersOnJoinPct)}
                            sx={{
                              bgcolor: row.belowMinPlayersOnJoinPct >= 70
                                ? 'rgba(255,79,105,0.15)'
                                : 'rgba(255,165,2,0.15)',
                              color: row.belowMinPlayersOnJoinPct >= 70 ? '#ff6b78' : '#e8a22a',
                              fontWeight: 600,
                              fontSize: '0.68rem',
                              height: 22
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>{pctFmt(row.playRatePct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Card>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MultiplayerLiquidityDashboard;

